import { Link } from 'react-router-dom';
import CurrencySelector from '@/components/CurrencySelector';
import ThemeToggle from '@/components/ThemeToggle';
import { ReviewsSection } from '@/components/property/ReviewsSection';
import { ReviewFormDialog } from '@/components/property/ReviewFormDialog';
import { BookingSidebar } from '@/components/property/BookingSidebar';
import { PropertyInfoSection } from '@/components/property-detail/PropertyInfoSection';
import { PropertyLocationCard } from '@/components/property-detail/PropertyLocationCard';
import { PropertyPhotoGallery } from '@/components/property-detail/PropertyPhotoGallery';
import { BookingStatusBanner } from '@/components/property-detail/BookingStatusBanner';
import { useBranding } from '@/contexts/BrandingContext';
import { BrandMark } from '@/components/BrandLogo';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { usePropertyDetail } from '@/hooks/usePropertyDetail';

const PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/35264654/pexels-photo-35264654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1572177215152-32f247303126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1765775635143-6462630748ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
];

export default function PropertyDetailPage() {
  const { branding: brandData } = useBranding();
  const {
    property, loading, navigate,
    selectedDates, setSelectedDates,
    processingPayment, bookingRequestStatus, bookingRequestData,
    couponCode, setCouponCode, appliedCoupon, validatingCoupon,
    guestCounts, maxGuests, guestPhone, setGuestPhone,
    reviews, reviewAverages, reviewTotal,
    showReviewForm, setShowReviewForm,
    submittingReview, reviewForm, setReviewForm,
    reviewPhotoUploading, blockedDates,
    isFavorite, favCount, paymentInfo,
    approvedRequest, payingApproved, activeRequest, propertyId,
    nights, total, deposit, depositLabel,
    handlePayMercadoPago, handlePayBancard, copyToClipboard, toggleFavorite,
    validateCoupon, isDateBlocked, submitReview,
    submitAdminResponse, handleReviewPhotoUpload,
    updateGuestCount, handlePayment, setBookingRequestStatus,
    isAuthenticated, login, t,
  } = usePropertyDetail();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F4F0] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#264639] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!property) return null;

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <BrandMark onDark={false} className="w-10 h-10" alt={brandData?.site_name || 'Estadia 365'} />
              <span className="font-bold text-xl text-[#1C1917]" style={{ fontFamily: 'Outfit' }}>{brandData?.site_name || 'Estadia 365'}</span>
            </Link>
            <div className="flex items-center space-x-4">
              <CurrencySelector className="hidden sm:flex" />
              {isAuthenticated ? (
                <Link to="/dashboard"><Button className="bg-[#264639] hover:bg-[#1C352B]">Dashboard</Button></Link>
              ) : (
                <Button onClick={login} className="bg-[#264639] hover:bg-[#1C352B]">{t('nav.login')}</Button>
              )}
              <ThemeToggle />
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 text-[#57534E] hover:text-[#1C1917]" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Volver
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              {/* Photos */}
              <PropertyPhotoGallery property={property} />

              <PropertyInfoSection
                property={property} isAuthenticated={isAuthenticated}
                isFavorite={isFavorite} favCount={favCount}
                onToggleFavorite={toggleFavorite} t={t}
              />

              <PropertyLocationCard property={property} t={t} />

              <ReviewsSection reviews={reviews} reviewAverages={reviewAverages} reviewTotal={reviewTotal}
                onWriteReview={() => setShowReviewForm(true)} onSubmitAdminResponse={submitAdminResponse} />

              <ReviewFormDialog open={showReviewForm} onOpenChange={setShowReviewForm}
                reviewForm={reviewForm} setReviewForm={setReviewForm}
                onSubmit={submitReview} submitting={submittingReview}
                onPhotoUpload={handleReviewPhotoUpload} photoUploading={reviewPhotoUploading} />
            </div>

            <div className="space-y-4">
              {(activeRequest || bookingRequestStatus === 'pending') && (
                <BookingStatusBanner
                  requestData={activeRequest || bookingRequestData}
                  propertyId={propertyId}
                  onDismiss={bookingRequestStatus === 'pending' ? () => setBookingRequestStatus(null) : undefined}
                />
              )}
              <BookingSidebar property={property} selectedDates={selectedDates} onSelectDates={setSelectedDates}
                nights={nights} total={total} deposit={deposit} depositLabel={depositLabel}
                guestCounts={guestCounts} maxGuests={maxGuests} onUpdateGuestCount={updateGuestCount}
                guestPhone={guestPhone} onGuestPhoneChange={setGuestPhone}
                couponCode={couponCode} onCouponCodeChange={setCouponCode}
                onValidateCoupon={validateCoupon} validatingCoupon={validatingCoupon} appliedCoupon={appliedCoupon}
                onPayment={handlePayment} processingPayment={processingPayment || bookingRequestStatus === 'sending'}
                blockedDates={blockedDates} isDateBlocked={isDateBlocked}
                paymentInfo={paymentInfo} copyToClipboard={copyToClipboard} bookingRequestStatus={bookingRequestStatus} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
